import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { MeetingBooksLinkApiService } from '../services/meeting-books-link-api.service';
import { MeetingBooksLinkResponse } from '../models/meeting.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-booking-link-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
  ],
  templateUrl: './booking-link-list.component.html',
})
export class BookingLinkListComponent implements OnInit {
  links: MeetingBooksLinkResponse[] = [];
  loading = false;
  error = '';

  // Filtros
  filtroActive: '' | 'true' | 'false' = '';
  filtroSearch = '';

  constructor(
    private api: MeetingBooksLinkApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.loading = true;
    this.error = '';

    this.api
      .filter({
        businessId,
        isActive:
          this.filtroActive === '' ? undefined : this.filtroActive === 'true',
      })
      .subscribe({
        next: (data) => {
          // INC 61: ordenar por última actualización, más reciente primero.
          this.links = this.sortByUpdatedDesc(this.filterSearch(data));
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Error al cargar booking links';
          this.loading = false;
        },
      });
  }

  /** INC 61: más recientemente actualizados primero (fallback a fecha de creación). */
  private sortByUpdatedDesc(list: MeetingBooksLinkResponse[]): MeetingBooksLinkResponse[] {
    const ts = (l: MeetingBooksLinkResponse): number => {
      const raw = l.datetimeUpdated || l.datetimeCreated;
      const t = raw ? new Date(raw).getTime() : 0;
      return Number.isNaN(t) ? 0 : t;
    };
    return [...list].sort((a, b) => ts(b) - ts(a));
  }

  private filterSearch(list: MeetingBooksLinkResponse[]): MeetingBooksLinkResponse[] {
    const q = this.filtroSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (l) =>
        l.slug?.toLowerCase().includes(q) ||
        l.title?.toLowerCase().includes(q) ||
        l.description?.toLowerCase().includes(q)
    );
  }

  crear(): void {
    this.router.navigate(['/meeting/booking-links/crear']);
  }

  editar(link: MeetingBooksLinkResponse): void {
    this.router.navigate(['/meeting/booking-links/editar', link.id]);
  }

  toggleActive(link: MeetingBooksLinkResponse): void {
    // INC 62: usar PATCH /{id}/active con body { active } (evita el 405 del endpoint anterior).
    const nextActive = !link.isActive;
    this.api.setActive(link.id, nextActive).subscribe({
      next: () => {
        this.toast.success(nextActive ? 'Página activada' : 'Página desactivada');
        this.cargar();
      },
      error: (err) =>
        this.toast.error({
          title: 'Error',
          message:
            err.error?.message ||
            'No se pudo cambiar el estado de la página de reserva.',
        }),
    });
  }

  async eliminar(link: MeetingBooksLinkResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar booking link',
      message: `¿Eliminar el booking link "${link.title || link.slug}"?`
    });
    if (!ok) return;
    this.api.delete(link.id).subscribe({
      next: () => { this.cargar(); this.toast.deleted('Enlace de reserva'); },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' }); },
    });
  }

  copiarUrl(link: MeetingBooksLinkResponse): void {
    // INC 38: usar siempre la URL del frontend con hash-routing, no el fullUrl del backend
    const url = this.buildPublicUrl(link.slug);
    if (!url) return;
    navigator.clipboard?.writeText(url).then(
      () => this.toast.success('Enlace copiado al portapapeles'),
      () => this.toast.error({ title: 'Error', message: 'No se pudo copiar el enlace' })
    );
  }

  buildPublicUrl(slug?: string | null): string {
    if (!slug) return '';
    // INC 38: ruta pública de la SPA — hash-routing (/#/book/:slug), origin del frontend
    const origin = window.location.origin.replace(/\/$/, '');
    return `${origin}/#/book/${slug}`;
  }

  getSchedulingLabel(mode?: string): string {
    switch (mode) {
      case 'one_on_one': return '1:1';
      case 'round_robin': return 'Round robin';
      case 'round_robin_weighted': return 'Round robin ponderado';
      case 'collective': return 'Colectivo';
      case 'group_event': return 'Evento grupal';
      default: return mode || '—';
    }
  }
}
